/**
 * Publish validation for Rules Engine drafts (V1 + V2).
 *
 * V1 (schemaVersion 1): lifecycle domains, fixed event kinds, V1 object
 * filters, required action keys — existing validation preserved.
 *
 * V2 (schemaVersion 2, or undefined+has operation): generic resource
 * trigger, RESOURCE_TYPE_REGISTRY / RESOURCE_FIELD_REGISTRY / TRIGGER_OPERATIONS,
 * no action keys, no enabled/alwaysRun on branches, immediate-only timing.
 *
 * Both versions share the three notification action types: `send-notification`,
 * `send-email`, and `assign-template-forms`. Domain `claim-assembly` uses
 * `set-claim-field` instead.
 */
import {
  CLAIM_GRAPH_WRITE_TOKENS,
  CLAIM_WRITE_FIELD_PATHS,
  CONDITION_FIELDS,
  CONDITION_OPERATORS,
  EMAIL_RECIPIENT_POLICIES,
  FORM_ASSIGNMENT_RECIPIENT_POLICIES,
  NOTIFICATION_RECIPIENT_POLICIES,
  REQUIRED_TRIGGER_FILTERS,
  RESOURCE_FIELD_REGISTRY,
  RESOURCE_TYPE_REGISTRY,
  RULE_ACTION_KEYS,
  RECIPIENT_POLICIES,
  RULE_PACK_DOMAINS,
  SCHEDULED_TIMING_REFERENCE_FIELDS,
  TRIGGER_EVENT_KINDS,
  TRIGGER_EVENTS_BY_RESOURCE,
  TRIGGER_FILTER_KINDS_BY_RESOURCE,
  TRIGGER_OPERATIONS,
  TRIGGER_RESOURCE_TYPES,
  allowedActionTypes,
  getClaimWriteField,
  isClaimAssemblyDomain,
  isClaimGraphTokenCompatible,
  type ActionType,
  type RuleAction,
  type RuleActionKey,
  type RulePackDomain,
  type RulePackDraft,
  type TriggerEventKind,
  type TriggerObjectFilter,
  type TriggerResourceType,
  type ValidationError,
  type ValidationResult,
  type WorkflowBranch,
  type WorkflowStep,
} from '../types';

function isFilterComplete(filter: TriggerObjectFilter): boolean {
  switch (filter.kind) {
    case 'questionnaire-canonical':
    case 'questionnaire-id':
      return !!(filter.value?.trim() || filter.code?.trim());
    case 'service-request-code':
    case 'service-request-category':
    case 'task-code':
      return !!(filter.system?.trim() && filter.code?.trim());
    case 'episode-type':
      return !!(filter.code?.trim());
    default:
      return false;
  }
}

function validateFilterCompleteness(
  filter: TriggerObjectFilter,
  errors: ValidationError[],
): void {
  switch (filter.kind) {
    case 'questionnaire-canonical':
    case 'questionnaire-id':
      if (!filter.value?.trim() && !filter.code?.trim()) {
        errors.push({ path: 'trigger.objectFilters', message: `Filter "${filter.kind}" must have a value.` });
      }
      break;
    case 'service-request-code':
    case 'service-request-category':
      if (!filter.system?.trim()) {
        errors.push({ path: 'trigger.objectFilters', message: `Filter "${filter.kind}" must have a code system.` });
      }
      if (!filter.code?.trim()) {
        errors.push({ path: 'trigger.objectFilters', message: `Filter "${filter.kind}" must have a code value.` });
      }
      break;
    case 'episode-type':
      if (!filter.code?.trim()) {
        errors.push({ path: 'trigger.objectFilters', message: `Filter "${filter.kind}" must have a code value.` });
      }
      break;
    case 'task-code':
      if (!filter.system?.trim()) {
        errors.push({ path: 'trigger.objectFilters', message: `Filter "${filter.kind}" must have a code system.` });
      }
      if (!filter.code?.trim()) {
        errors.push({ path: 'trigger.objectFilters', message: `Filter "${filter.kind}" must have a code value.` });
      }
      break;
  }
}

const V1_DOMAINS = new Set([...RULE_PACK_DOMAINS.map((d) => d.value), 'client-lifecycle' as const]);
const V1_RESOURCE_TYPES = new Set(TRIGGER_RESOURCE_TYPES.map((r) => r.value));
const V1_EVENT_KINDS = new Set(TRIGGER_EVENT_KINDS.map((e) => e.value));
const V1_CONDITION_FIELDS = new Set(CONDITION_FIELDS.map((f) => f.value));
const V1_CONDITION_OPERATORS = new Set(CONDITION_OPERATORS.map((o) => o.value));
const V1_ACTION_KEYS = new Set(RULE_ACTION_KEYS.map((k) => k.value));
const V1_NOTIFICATION_RECIPIENT_POLICIES = new Set(NOTIFICATION_RECIPIENT_POLICIES.map((r) => r.value));
const V1_ACTION_TYPES = new Set<ActionType>([
  'send-notification',
  'send-email',
  'assign-template-forms',
  'set-claim-field',
]);

// V2 lookup sets
const V2_RESOURCE_TYPES = new Set(RESOURCE_TYPE_REGISTRY.map((r) => r.resourceType));
const V2_OPERATIONS = new Set(TRIGGER_OPERATIONS.map((o) => o.value));
const V2_FIELD_PATHS = new Set(RESOURCE_FIELD_REGISTRY.map((f) => f.fieldPath));

function isV2Rule(draft: RulePackDraft): boolean {
  if (draft.schemaVersion === 2) return true;
  if (draft.schemaVersion === 1) return false;
  return (!!draft.trigger.operations?.length || !!draft.trigger.operation) && draft.trigger.eventKinds.length === 0;
}

const ACTION_KEY_TYPE_PREFIX: Record<string, ActionType> = {
  'notify.': 'send-notification',
  'email.': 'send-email',
  'assign_forms.': 'assign-template-forms',
};

function expectedActionTypeForKey(key: RuleActionKey): ActionType | undefined {
  for (const [prefix, type] of Object.entries(ACTION_KEY_TYPE_PREFIX)) {
    if (key.startsWith(prefix)) return type;
  }
  return undefined;
}

export function validateRulePackDraft(draft: RulePackDraft): ValidationResult {
  const errors: ValidationError[] = [];
  const v2 = isV2Rule(draft);

  if (!draft.title.trim()) {
    errors.push({ path: 'title', message: 'Rule must have a non-empty title.' });
  }

  if (!V1_DOMAINS.has(draft.domain)) {
    errors.push({ path: 'domain', message: `Domain "${draft.domain}" is not supported. Allowed: ${[...V1_DOMAINS].join(', ')}.` });
  }

  // --- Trigger validation (V1 vs V2) ---

  if (draft.trigger.resourceTypes.length === 0) {
    errors.push({ path: 'trigger.resourceTypes', message: 'Trigger must have at least one resource type.' });
  }

  if (v2) {
    for (const rt of draft.trigger.resourceTypes) {
      if (!V2_RESOURCE_TYPES.has(rt)) {
        errors.push({ path: 'trigger.resourceTypes', message: `Resource type "${rt}" is not in the resource registry.` });
      }
    }

    const operations = draft.trigger.operations?.length
      ? draft.trigger.operations
      : (draft.trigger.operation ? [draft.trigger.operation] : []);
    if (operations.length === 0) {
      errors.push({ path: 'trigger.operations', message: 'Trigger must have at least one operation (created, updated, or manual-run).' });
    }
    for (const operation of operations) {
      if (!V2_OPERATIONS.has(operation)) {
        errors.push({ path: 'trigger.operations', message: `Operation "${operation}" is not valid. Allowed: ${[...V2_OPERATIONS].join(', ')}.` });
      }
      for (const rt of draft.trigger.resourceTypes) {
        const supported = RESOURCE_TYPE_REGISTRY.find((entry) => entry.resourceType === rt)?.supportedOperations ?? [];
        if (!supported.includes(operation)) {
          errors.push({ path: 'trigger.operations', message: `Operation "${operation}" is not supported for ${rt}.` });
        }
      }
    }

    for (const filter of draft.trigger.filters ?? []) {
      if (!V2_FIELD_PATHS.has(filter.fieldPath)) {
        errors.push({ path: 'trigger.filters', message: `Filter field "${filter.fieldPath}" is not in the resource field registry.` });
      }
    }
  } else {
    // V1 trigger validation
    for (const rt of draft.trigger.resourceTypes) {
      if (!V1_RESOURCE_TYPES.has(rt)) {
        errors.push({ path: 'trigger.resourceTypes', message: `Resource type "${rt}" is not supported in V1.` });
      }
    }

    if (draft.trigger.eventKinds.length === 0) {
      errors.push({ path: 'trigger.eventKinds', message: 'Trigger must have at least one event.' });
    }
    for (const ek of draft.trigger.eventKinds) {
      if (!V1_EVENT_KINDS.has(ek)) {
        errors.push({ path: 'trigger.eventKinds', message: `Event "${ek}" is not supported in V1.` });
      }
    }

    for (const rt of draft.trigger.resourceTypes) {
      const validEvents = TRIGGER_EVENTS_BY_RESOURCE[rt as TriggerResourceType] ?? [];
      for (const ek of draft.trigger.eventKinds) {
        if (!validEvents.includes(ek as TriggerEventKind)) {
          errors.push({
            path: 'trigger',
            message: `Event "${ek}" is not valid for resource type "${rt}". Valid events: ${validEvents.join(', ') || 'none'}.`,
          });
        }
      }
    }

    const v1Filters = draft.trigger.objectFilters ?? [];
    for (const filter of v1Filters) {
      const allowedForResource = Object.entries(TRIGGER_FILTER_KINDS_BY_RESOURCE)
        .filter(([rt]) => draft.trigger.resourceTypes.includes(rt as TriggerResourceType))
        .flatMap(([, kinds]) => kinds);
      if (!allowedForResource.includes(filter.kind)) {
        errors.push({
          path: 'trigger.objectFilters',
          message: `Filter kind "${filter.kind}" is not applicable for the selected resource types.`,
        });
      }
      validateFilterCompleteness(filter, errors);
    }

    for (const ek of draft.trigger.eventKinds) {
      const required = REQUIRED_TRIGGER_FILTERS[ek as TriggerEventKind];
      if (!required) continue;
      for (const rt of draft.trigger.resourceTypes) {
        const requiredKinds = required[rt as TriggerResourceType];
        if (!requiredKinds || requiredKinds.length === 0) continue;
        const hasCompleteFilter = v1Filters.some(
          (f) => requiredKinds.includes(f.kind) && isFilterComplete(f),
        );
        if (!hasCompleteFilter) {
          errors.push({
            path: 'trigger.objectFilters',
            message: `Trigger "${ek}" with resource "${rt}" requires a complete object/type filter (${requiredKinds.join(' or ')}). Select the specific object/type that triggers this rule.`,
          });
        }
      }
    }
  }

  // --- Workflow / rung validation ---

  if (draft.steps && draft.steps.length > 0) {
    validateWorkflowSteps(draft.steps, 'workflow', errors, v2, draft.domain);
  } else {
    if (draft.rungs.length === 0) {
      errors.push({ path: 'rungs', message: 'At least one branch is required.' });
    }

    for (let ri = 0; ri < draft.rungs.length; ri++) {
      const rung = draft.rungs[ri];
      const rungPath = `rungs[${ri}]`;

      // V1: skip disabled rungs; V2: enabled is deprecated, validate all
      if (!v2 && !rung.enabled) continue;

      // V1: require conditions or alwaysRun; V2: empty conditionGroups = unconditional (allowed)
      if (!v2 && !rung.alwaysRun && rung.conditionGroups.length === 0) {
        errors.push({ path: `${rungPath}.conditionGroups`, message: `Branch "${rung.name || ri + 1}" must have at least one condition group or be marked as "Always run."` });
      }

      for (let gi = 0; gi < rung.conditionGroups.length; gi++) {
        const group = rung.conditionGroups[gi];
        const groupPath = `${rungPath}.conditionGroups[${gi}]`;

        if (group.conditions.length === 0) {
          errors.push({ path: groupPath, message: `Condition group ${gi + 1} in branch "${rung.name || ri + 1}" must have at least one condition.` });
        }

        for (let ci = 0; ci < group.conditions.length; ci++) {
          const cond = group.conditions[ci];
          const condPath = `${groupPath}.conditions[${ci}]`;

          if (v2) {
            if (!V2_FIELD_PATHS.has(cond.field) && !V1_CONDITION_FIELDS.has(cond.field)) {
              errors.push({ path: `${condPath}.field`, message: `Condition field "${cond.field}" is not in the field registry.` });
            }
          } else {
            if (!V1_CONDITION_FIELDS.has(cond.field)) {
              errors.push({ path: `${condPath}.field`, message: `Condition field "${cond.field}" is not supported in V1.` });
            }
          }
          if (!V1_CONDITION_OPERATORS.has(cond.operator)) {
            errors.push({ path: `${condPath}.operator`, message: `Operator "${cond.operator}" is not supported.` });
          }
        }
      }

      if (rung.actions.length === 0) {
        errors.push({ path: `${rungPath}.actions`, message: `Branch "${rung.name || ri + 1}" must have at least one action.` });
      }

      for (let ai = 0; ai < rung.actions.length; ai++) {
        const action = rung.actions[ai];
        const actionPath = `${rungPath}.actions[${ai}]`;

        if (!V1_ACTION_TYPES.has(action.type)) {
          errors.push({ path: `${actionPath}.type`, message: `Action type "${action.type}" is not supported.` });
          continue;
        }

        validateActionTypeForDomain(action.type, draft.domain, actionPath, errors);
        validateNoScheduledFormAssignment(action, actionPath, errors);

        if (v2) {
          validateActionKeyIfPresent(action, actionPath, errors);
          validateTimingBucket(action, actionPath, errors, draft.trigger.resourceTypes as TriggerResourceType[]);
        } else {
          if (!action.actionKey) {
            errors.push({ path: `${actionPath}.actionKey`, message: 'Action must have an action key.' });
          } else if (!V1_ACTION_KEYS.has(action.actionKey)) {
            errors.push({ path: `${actionPath}.actionKey`, message: `Action key "${action.actionKey}" is not a valid V1 action key.` });
          } else {
            const expectedType = expectedActionTypeForKey(action.actionKey);
            if (expectedType && expectedType !== action.type) {
              errors.push({ path: `${actionPath}.actionKey`, message: `Action key "${action.actionKey}" requires action type "${expectedType}" but got "${action.type}".` });
            }
          }
        }

        if (action.type === 'send-notification') {
          validateNotificationAction(action, actionPath, errors);
        } else if (action.type === 'send-email') {
          validateEmailAction(action, actionPath, errors);
        } else if (action.type === 'assign-template-forms') {
          validateFormAssignmentAction(action, actionPath, errors);
        } else if (action.type === 'set-claim-field') {
          validateSetClaimFieldAction(action, actionPath, errors);
        }
        validateFormsAlreadyAssignedFlag(action, rung.actions, actionPath, errors);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/** `skipIfFormsAlreadyAssigned` is valid only on a send-email action in a rung that also has an assign-template-forms action. */
function validateFormsAlreadyAssignedFlag(
  action: RuleAction,
  rungActions: RuleAction[],
  actionPath: string,
  errors: ValidationError[],
): void {
  if (!action.skipIfFormsAlreadyAssigned) return;
  if (action.type !== 'send-email' || !rungActions.some((a) => a.type === 'assign-template-forms')) {
    errors.push({
      path: `${actionPath}.skipIfFormsAlreadyAssigned`,
      message: '"Skip if forms already assigned" is valid only on a send-email action in a branch that also assigns forms.',
    });
  }
  if (action.type === 'send-email' && action.scheduledTiming) {
    errors.push({
      path: `${actionPath}.skipIfFormsAlreadyAssigned`,
      message: '"Skip if forms already assigned" is only meaningful on an immediate email: a deferred email is checked when due, after the branch\'s assignment has run, and would always be suppressed.',
    });
  }
}

/** V2: action key is optional; if present, verify it matches the action type. */
function validateActionKeyIfPresent(
  action: RuleAction,
  actionPath: string,
  errors: ValidationError[],
): void {
  if (!action.actionKey) return;
  const expectedType = expectedActionTypeForKey(action.actionKey);
  if (expectedType && expectedType !== action.type) {
    errors.push({ path: `${actionPath}.actionKey`, message: `Action key "${action.actionKey}" requires action type "${expectedType}" but got "${action.type}".` });
  }
}

/** A delayed form assignment is not offered (ENG-3764 D29): the runtime suppresses one, so a rule may not save it. */
function validateNoScheduledFormAssignment(action: RuleAction, actionPath: string, errors: ValidationError[]): void {
  if (action.type === 'assign-template-forms' && (action.timingBucket === 'scheduled' || action.scheduledTiming)) {
    errors.push({ path: `${actionPath}.timingBucket`, message: 'Form assignments run immediately; delayed form assignment is not supported.' });
  }
}

/** V2: allow 'immediate' and 'scheduled' timing buckets. */
function validateTimingBucket(
  action: RuleAction,
  actionPath: string,
  errors: ValidationError[],
  triggerResourceTypes?: TriggerResourceType[],
): void {
  if (action.timingBucket && action.timingBucket !== 'immediate' && action.timingBucket !== 'scheduled') {
    errors.push({ path: `${actionPath}.timingBucket`, message: `Timing "${action.timingBucket}" is not supported. Allowed: "immediate", "scheduled".` });
  }
  if (action.timingBucket === 'scheduled' && action.type !== 'assign-template-forms') {
    validateScheduledTiming(action, actionPath, errors, triggerResourceTypes);
  }
}

function validateScheduledTiming(
  action: RuleAction,
  actionPath: string,
  errors: ValidationError[],
  triggerResourceTypes?: TriggerResourceType[],
): void {
  const t = action.scheduledTiming;
  if (!t) {
    errors.push({ path: `${actionPath}.scheduledTiming`, message: 'Scheduled timing requires a timing configuration (direction, offset, reference field).' });
    return;
  }
  if (!['before', 'after', 'at'].includes(t.direction)) {
    errors.push({ path: `${actionPath}.scheduledTiming.direction`, message: `Invalid direction "${t.direction}". Allowed: before, after, at.` });
  }
  if (t.direction !== 'at' && (typeof t.offsetAmount !== 'number' || t.offsetAmount <= 0)) {
    errors.push({ path: `${actionPath}.scheduledTiming.offsetAmount`, message: 'Offset amount must be a positive number.' });
  }
  if (!['minutes', 'hours', 'days'].includes(t.offsetUnit)) {
    errors.push({ path: `${actionPath}.scheduledTiming.offsetUnit`, message: `Invalid offset unit "${t.offsetUnit}". Allowed: minutes, hours, days.` });
  }
  if (!t.referenceField) {
    errors.push({ path: `${actionPath}.scheduledTiming.referenceField`, message: 'Reference field is required for scheduled timing.' });
  } else if (triggerResourceTypes && triggerResourceTypes.length > 0) {
    const validFields = SCHEDULED_TIMING_REFERENCE_FIELDS.filter((f) =>
      triggerResourceTypes.includes(f.resourceType),
    );
    if (validFields.length > 0 && !validFields.some((f) => f.fieldPath === t.referenceField)) {
      errors.push({
        path: `${actionPath}.scheduledTiming.referenceField`,
        message: `Reference field "${t.referenceField}" is not available for resource type(s) ${triggerResourceTypes.join(', ')}.`,
      });
    }
  }
}

function validateNotificationAction(
  action: { notificationRecipientPolicies?: string[]; notificationTitle?: string; notificationBody?: string; label?: string },
  actionPath: string,
  errors: ValidationError[],
): void {
  const policies = action.notificationRecipientPolicies ?? [];
  if (policies.length === 0) {
    errors.push({ path: `${actionPath}.notificationRecipientPolicies`, message: 'Notification action must have at least one recipient policy.' });
  }
  for (const rp of policies) {
    if (!V1_NOTIFICATION_RECIPIENT_POLICIES.has(rp as RecipientPolicyType)) {
      errors.push({ path: `${actionPath}.notificationRecipientPolicies`, message: `Recipient policy "${rp}" is not supported for in-app notification in V1. Supported: ${[...V1_NOTIFICATION_RECIPIENT_POLICIES].join(', ')}.` });
    }
  }

  const hasTitle = !!(action.notificationTitle?.trim() || action.label?.trim());
  if (!hasTitle) {
    errors.push({ path: `${actionPath}.notificationTitle`, message: 'Notification action must have a title (notificationTitle or label).' });
  }
}

const V1_EMAIL_RECIPIENT_POLICIES = new Set(EMAIL_RECIPIENT_POLICIES.map((r) => r.value));

function validateEmailAction(
  action: { emailRecipientPolicies?: string[]; emailTemplateRef?: { id: string; title?: string } },
  actionPath: string,
  errors: ValidationError[],
): void {
  if (!action.emailTemplateRef?.id) {
    errors.push({ path: `${actionPath}.emailTemplateRef`, message: 'Email action must have a template selected from Email Notifications.' });
  }
  const policies = action.emailRecipientPolicies ?? [];
  if (policies.length === 0) {
    errors.push({ path: `${actionPath}.emailRecipientPolicies`, message: 'Email action must have at least one recipient policy.' });
  }
  for (const rp of policies) {
    if (!V1_EMAIL_RECIPIENT_POLICIES.has(rp as RecipientPolicyType)) {
      errors.push({ path: `${actionPath}.emailRecipientPolicies`, message: `Recipient policy "${rp}" is not supported for email in V1. Supported: ${[...V1_EMAIL_RECIPIENT_POLICIES].join(', ')}.` });
    }
  }
}

type RecipientPolicyType = (typeof RECIPIENT_POLICIES)[number]['value'];

const V1_SUPPORTED_ASSIGNMENT_POLICIES = new Set(FORM_ASSIGNMENT_RECIPIENT_POLICIES.map((p) => p.value));

function validateFormAssignmentAction(
  action: { formTemplateRefs?: { id: string }[]; assignmentRecipientPolicies?: string[]; assignmentDueOffsetDays?: number },
  actionPath: string,
  errors: ValidationError[],
): void {
  if (!action.formTemplateRefs || action.formTemplateRefs.length === 0) {
    errors.push({ path: `${actionPath}.formTemplateRefs`, message: 'Form assignment action must have at least one form/template selected.' });
  }

  const policies = action.assignmentRecipientPolicies ?? [];
  if (policies.length === 0) {
    errors.push({ path: `${actionPath}.assignmentRecipientPolicies`, message: 'Form assignment action must have at least one assignment recipient policy.' });
  }
  for (const rp of policies) {
    if (!V1_SUPPORTED_ASSIGNMENT_POLICIES.has(rp as RecipientPolicyType)) {
      errors.push({
        path: `${actionPath}.assignmentRecipientPolicies`,
        message: `Assignment recipient policy "${rp}" is not supported for form assignment in V1. Only supported: ${[...V1_SUPPORTED_ASSIGNMENT_POLICIES].join(', ')}.`,
      });
    }
  }

  if (action.assignmentDueOffsetDays !== undefined && action.assignmentDueOffsetDays < 0) {
    errors.push({ path: `${actionPath}.assignmentDueOffsetDays`, message: 'Due offset days must be non-negative.' });
  }
}

function validateActionTypeForDomain(
  actionType: ActionType,
  domain: RulePackDomain,
  actionPath: string,
  errors: ValidationError[],
): void {
  const allowed = allowedActionTypes(domain);
  if (!allowed.includes(actionType)) {
    errors.push({
      path: `${actionPath}.type`,
      message: isClaimAssemblyDomain(domain)
        ? 'Claim Assembly rules may only use the Set Claim field action.'
        : `Action type "${actionType}" is not allowed for domain "${domain}".`,
    });
  }
}

function validateSetClaimFieldAction(
  action: RuleAction,
  actionPath: string,
  errors: ValidationError[],
): void {
  const path = action.claimFieldPath?.trim() ?? '';
  if (!path) {
    errors.push({ path: `${actionPath}.claimFieldPath`, message: 'Set Claim field action must select a Claim field.' });
    return;
  }
  if (!CLAIM_WRITE_FIELD_PATHS.has(path)) {
    errors.push({ path: `${actionPath}.claimFieldPath`, message: `Claim field "${path}" is not in the write registry.` });
    return;
  }
  const value = action.claimFieldValue?.trim() ?? '';
  if (!value) {
    errors.push({ path: `${actionPath}.claimFieldValue`, message: 'Set Claim field action must have a value.' });
    return;
  }
  const writeField = getClaimWriteField(path);
  const source = action.claimFieldValueSource === 'graph' ? 'graph' : 'literal';
  if (source === 'graph') {
    if (!CLAIM_GRAPH_WRITE_TOKENS.has(value)) {
      errors.push({ path: `${actionPath}.claimFieldValue`, message: `Graph token "${value}" is not in the write catalog.` });
      return;
    }
    if (!isClaimGraphTokenCompatible(path, value)) {
      errors.push({
        path: `${actionPath}.claimFieldValue`,
        message: `Graph token "${value}" is not compatible with Claim field "${path}".`,
      });
    }
    return;
  }
  if (writeField?.valueType === 'coding' && !action.claimFieldSystem?.trim()) {
    errors.push({
      path: `${actionPath}.claimFieldSystem`,
      message: 'This Claim field requires a code system.',
    });
  }
}

export function isPublishableRulePackDraft(draft: RulePackDraft): boolean {
  return validateRulePackDraft(draft).valid;
}

// ---------------------------------------------------------------------------
// Recursive workflow step validation
// ---------------------------------------------------------------------------

function validateWorkflowSteps(
  steps: WorkflowStep[],
  pathPrefix: string,
  errors: ValidationError[],
  v2: boolean,
  domain: RulePackDomain,
): void {
  // The same step list is the workflow equivalent of a rung: a flag's sibling assign action must be in it.
  const siblingActions = steps.flatMap((s) => (s.kind === 'action' ? [s.action] : []));
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const stepPath = `${pathPrefix}[${i}]`;

    if (step.kind === 'action') {
      validateStepAction(step.action, stepPath, errors, v2, domain, siblingActions);
    } else if (step.kind === 'branch') {
      if (step.branches.length === 0) {
        errors.push({ path: `${stepPath}.branches`, message: 'Branch split must have at least one IF branch.' });
      }
      for (let bi = 0; bi < step.branches.length; bi++) {
        validateWorkflowBranch(step.branches[bi], `${stepPath}.branches[${bi}]`, errors, v2, domain);
      }
      if (step.elseSteps.length > 0) {
        validateWorkflowSteps(step.elseSteps, `${stepPath}.elseSteps`, errors, v2, domain);
      }
    }
  }
}

function validateWorkflowBranch(
  branch: WorkflowBranch,
  pathPrefix: string,
  errors: ValidationError[],
  v2: boolean,
  domain: RulePackDomain,
): void {
  // V1: skip disabled branches; V2: enabled is deprecated, validate all
  if (!v2 && !branch.enabled) return;

  // V1: require conditions or alwaysRun; V2: empty conditionGroups = unconditional (allowed)
  if (!v2 && !branch.alwaysRun && branch.conditionGroups.length === 0) {
    errors.push({
      path: `${pathPrefix}.conditionGroups`,
      message: `Branch "${branch.name}" must have at least one condition group or be marked as "Always run."`,
    });
  }

  for (let gi = 0; gi < branch.conditionGroups.length; gi++) {
    const group = branch.conditionGroups[gi];
    const groupPath = `${pathPrefix}.conditionGroups[${gi}]`;

    if (group.conditions.length === 0) {
      errors.push({ path: groupPath, message: `Condition group ${gi + 1} in branch "${branch.name}" must have at least one condition.` });
    }

    for (let ci = 0; ci < group.conditions.length; ci++) {
      const cond = group.conditions[ci];
      const condPath = `${groupPath}.conditions[${ci}]`;

      if (v2) {
        if (!V2_FIELD_PATHS.has(cond.field) && !V1_CONDITION_FIELDS.has(cond.field)) {
          errors.push({ path: `${condPath}.field`, message: `Condition field "${cond.field}" is not in the field registry.` });
        }
      } else {
        if (!V1_CONDITION_FIELDS.has(cond.field)) {
          errors.push({ path: `${condPath}.field`, message: `Condition field "${cond.field}" is not supported in V1.` });
        }
      }
      if (!V1_CONDITION_OPERATORS.has(cond.operator)) {
        errors.push({ path: `${condPath}.operator`, message: `Operator "${cond.operator}" is not supported.` });
      }
    }
  }

  validateWorkflowSteps(branch.steps, `${pathPrefix}.steps`, errors, v2, domain);
}

function validateStepAction(
  action: RuleAction,
  stepPath: string,
  errors: ValidationError[],
  v2: boolean,
  domain: RulePackDomain,
  siblingActions: RuleAction[],
): void {
  const actionPath = `${stepPath}.action`;

  if (!V1_ACTION_TYPES.has(action.type)) {
    errors.push({ path: `${actionPath}.type`, message: `Action type "${action.type}" is not supported.` });
    return;
  }

  validateActionTypeForDomain(action.type, domain, actionPath, errors);
  validateNoScheduledFormAssignment(action, actionPath, errors);

  if (v2) {
    validateActionKeyIfPresent(action, actionPath, errors);
    validateTimingBucket(action, actionPath, errors);
  } else {
    if (!action.actionKey) {
      errors.push({ path: `${actionPath}.actionKey`, message: 'Action must have an action key.' });
    } else if (!V1_ACTION_KEYS.has(action.actionKey as never)) {
      errors.push({ path: `${actionPath}.actionKey`, message: `Action key "${action.actionKey}" is not a valid V1 action key.` });
    } else {
      const expectedType = expectedActionTypeForKey(action.actionKey);
      if (expectedType && expectedType !== action.type) {
        errors.push({ path: `${actionPath}.actionKey`, message: `Action key "${action.actionKey}" requires action type "${expectedType}" but got "${action.type}".` });
      }
    }
  }

  if (action.type === 'send-notification') {
    validateNotificationAction(action, actionPath, errors);
  } else if (action.type === 'send-email') {
    validateEmailAction(action, actionPath, errors);
  } else if (action.type === 'assign-template-forms') {
    validateFormAssignmentAction(action, actionPath, errors);
  } else if (action.type === 'set-claim-field') {
    validateSetClaimFieldAction(action, actionPath, errors);
  }
  validateFormsAlreadyAssignedFlag(action, siblingActions, actionPath, errors);
}
